<template>
  <div>
    <div class="mc-ai-sticky">
      <div class="mc-sectrail">
        <i
          v-for="(s, k) in sections"
          :key="s.key"
          :class="{ 'is-done': k < index, 'is-on': k === index }"
        ></i>
      </div>

      <div class="mc-ai-head">
        <h2 class="mc-h2">{{ section.title }}</h2>
        <span class="mc-tag mc-tag--brand">{{ index + 1 }} of {{ total }}</span>
      </div>

      <p class="mc-lede">
        Section {{ index + 1 }} — answer out loud, then check what we heard.
      </p>
    </div>

    <div class="mc-bubble">
      <div class="mc-who">
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M9 10h.01M15 10h.01M9 15c.8.7 1.9 1 3 1s2.2-.3 3-1" />
        </svg>
        APA AI claims officer
      </div>
      {{ section.intro }}
      <ul class="mc-asklist">
        <li v-for="ask in section.asks" :key="ask">{{ ask }}</li>
      </ul>
    </div>

    <div v-if="!unsupported" class="mc-center">
      <button
        type="button"
        class="mc-mic"
        :class="{ 'is-rec': recording }"
        :disabled="transcribing"
        :aria-label="recording ? 'Stop recording' : 'Record answer'"
        data-testid="ai-mic"
        @click="toggleRecording"
      >
        <svg
          v-if="recording"
          width="34"
          height="34"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <rect x="7" y="7" width="10" height="10" rx="2" />
        </svg>
        <svg
          v-else
          width="38"
          height="38"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
        >
          <path d="M12 15a3 3 0 003-3V6a3 3 0 10-6 0v6a3 3 0 003 3z" />
          <path d="M19 11a7 7 0 01-14 0M12 18v3" />
        </svg>
      </button>
      <div class="mc-wave" :class="{ 'is-on': recording }">
        <i v-for="n in 17" :key="n"></i>
      </div>
      <p class="mc-hint" :class="hintClass" data-testid="ai-mic-hint">
        {{ micHint }}
      </p>
      <button type="button" class="mc-linkbtn mc-mt8" @click="typeInstead">
        Prefer to type? Enter it yourself
      </button>
    </div>

    <div v-else class="mc-empty mc-mt24">
      <b>Recording isn’t available in this browser</b>
      No problem — type your answer instead.
      <button type="button" class="mc-linkbtn mc-mt8" @click="typeInstead">
        Enter it yourself
      </button>
    </div>

    <p v-if="error" class="mc-hint mc-hint--err" data-testid="ai-error">
      {{ error }}
      <button
        v-if="retryable"
        type="button"
        class="mc-linkbtn"
        @click="retryTranscription"
      >
        Try again
      </button>
    </p>

    <template v-if="captured">
      <div class="mc-trans mc-mt24">
        <div class="mc-transhead">
          <label class="mc-lbl2" :for="transcriptId"
            >What we heard — edit freely</label
          >
          <button type="button" class="mc-linkbtn" @click="reRecord">
            Re-record
          </button>
        </div>
        <textarea
          :id="transcriptId"
          ref="transcript"
          :value="transcript"
          data-testid="ai-transcript"
          @input="onTranscriptInput"
        ></textarea>
        <p class="mc-hint">
          Correct any names, places or numbers we got wrong — your edits are
          what we file.
        </p>
      </div>
    </template>

    <div v-else-if="!transcribing" class="mc-empty mc-mt24">
      <b>Nothing captured for this section yet</b>
      <span>
        Record your answer, or
        <button
          type="button"
          class="mc-linkbtn mc-linkbtn--inline"
          data-testid="ai-type-here"
          @click="typeInstead"
        >
          type it in</button
        >.
      </span>
    </div>
  </div>
</template>

<script>
import {
  MAX_RECORDING_SECONDS,
  isRecordingSupported,
  pickSupportedMimeType,
  extensionForMimeType,
  requestMicrophoneStream,
  buildRecorder,
  stopStreamTracks,
} from '@/utils/audioRecording'
import { transcribeAudio } from '@/services/claim-service'

export default {
  name: 'AiInterviewStep',
  props: {
    sections: { type: Array, required: true },
    index: { type: Number, required: true },
    // the interview state: { sec, t }
    value: { type: Object, required: true },
  },
  data() {
    return {
      recording: false,
      transcribing: false,
      unsupported: false,
      error: '',
      // kept so "Try again" re-posts the same audio instead of re-recording
      pending: null,
      stream: null,
      recorder: null,
      chunks: [],
      mimeType: '',
      elapsedSeconds: 0,
      timerInterval: null,
    }
  },
  computed: {
    section() {
      return this.sections[this.index]
    },
    total() {
      return this.sections.length
    },
    transcript() {
      return this.value.t[this.section.key] || ''
    },
    // per-section so the label's `for` can't collide
    transcriptId() {
      return `ai-transcript-${this.section.key}`
    },
    // undefined means "never captured"; '' is a real, deliberately empty answer
    captured() {
      return this.value.t[this.section.key] !== undefined
    },
    retryable() {
      return !!this.pending && !this.transcribing
    },
    micHint() {
      if (this.transcribing) return 'Transcribing…'
      if (this.recording) {
        return this.secondsLeft <= 30
          ? `Listening… ${this.formattedElapsed} — ${this.secondsLeft}s left`
          : `Listening… ${this.formattedElapsed} — tap to stop`
      }
      return this.captured ? 'Tap to record again' : 'Tap and start talking'
    },
    // amber as the cap nears, red in the last stretch
    hintClass() {
      if (!this.recording) return ''
      if (this.secondsLeft <= 10) return 'mc-hint--danger'
      if (this.secondsLeft <= 30) return 'mc-hint--warning'
      return ''
    },
    secondsLeft() {
      return MAX_RECORDING_SECONDS - this.elapsedSeconds
    },
    formattedElapsed() {
      const m = Math.floor(this.elapsedSeconds / 60)
      const s = Math.floor(this.elapsedSeconds % 60)
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },
  },
  created() {
    if (!isRecordingSupported()) this.unsupported = true
  },
  beforeDestroy() {
    this.stopTimer()
    stopStreamTracks(this.stream)
  },
  methods: {
    patch(changes) {
      this.$emit('input', { ...this.value, ...changes })
    },
    setTranscript(text) {
      this.patch({ t: { ...this.value.t, [this.section.key]: text } })
    },
    toggleRecording() {
      return this.recording ? this.stopRecording() : this.startRecording()
    },
    async startRecording() {
      this.error = ''
      try {
        this.stream = await requestMicrophoneStream()
      } catch (err) {
        this.error =
          err &&
          (err.name === 'NotAllowedError' ||
            err.name === 'PermissionDeniedError')
            ? 'We couldn’t access your microphone. Allow access in your browser’s site settings, or type your answer instead.'
            : 'Something went wrong starting the recording. Please try again, or type your answer instead.'
        return
      }
      this.chunks = []
      this.recorder = buildRecorder(this.stream)
      this.mimeType = this.recorder.mimeType || pickSupportedMimeType()
      this.recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) this.chunks.push(event.data)
      }
      // arrow wrapper: MediaRecorder binds `this` to the recorder, not the component
      this.recorder.onstop = () => this.onRecorderStop()
      this.recorder.start(1000)
      this.elapsedSeconds = 0
      this.recording = true
      this.startTimer()
    },
    startTimer() {
      this.timerInterval = setInterval(() => {
        this.elapsedSeconds += 1
        // hard stop: an unattended recording would otherwise run until the tab dies
        if (this.elapsedSeconds >= MAX_RECORDING_SECONDS) this.stopRecording()
      }, 1000)
    },
    stopTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
        this.timerInterval = null
      }
    },
    stopRecording() {
      if (this.recorder && this.recording) this.recorder.stop()
      stopStreamTracks(this.stream)
      this.stream = null
      this.recording = false
      this.stopTimer()
    },
    onRecorderStop() {
      const extension = extensionForMimeType(this.mimeType)
      this.pending = {
        blob: new Blob(this.chunks, { type: this.mimeType }),
        filename: `claim-${this.section.key}-${Date.now()}.${extension}`,
      }
      this.runTranscription()
    },
    async runTranscription() {
      this.transcribing = true
      this.error = ''
      try {
        const { data } = await transcribeAudio(this.pending)
        if (!data || !data.success) {
          this.error =
            (data && data.msg) ||
            'We couldn’t transcribe that recording. Try again, or type your answer instead.'
          return
        }
        // shown exactly as returned; extracting structured fields is a separate ticket
        this.setTranscript((data.data && data.data.content) || '')
        this.pending = null
        this.$emit('toast', 'Transcribed — please check it')
      } catch {
        this.error =
          'We couldn’t reach the transcription service. Check your connection and try again.'
      } finally {
        this.transcribing = false
      }
    },
    retryTranscription() {
      if (this.pending) this.runTranscription()
    },
    reRecord() {
      const t = { ...this.value.t }
      delete t[this.section.key]
      this.pending = null
      this.error = ''
      this.patch({ t })
    },
    typeInstead() {
      this.setTranscript(this.transcript)
      // the box only renders after the parent feeds state back, hence the tick
      this.$nextTick(() => {
        if (this.$refs.transcript) this.$refs.transcript.focus()
      })
    },
    onTranscriptInput(event) {
      this.setTranscript(event.target.value)
    },
  },
}
</script>

<style scoped>
/* Pinned so a long section can't scroll away where you are; the paired -20px margin and top cancel .mc-main's padding so it stays flush under the app bar in both states. */
.mc-ai-sticky {
  position: sticky;
  top: -20px;
  z-index: 2;
  margin: -20px 0 0;
  padding: 20px 0 12px;
  background: #f7f7fc;
}

.mc-ai-sticky .mc-lede {
  margin-bottom: 0;
}

.mc-sectrail {
  display: flex;
  gap: 6px;
  margin: 0 0 18px;
}

.mc-sectrail i {
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: #d9dbe9;
}

.mc-sectrail i.is-done {
  background: #27ae60;
}

.mc-sectrail i.is-on {
  background: #0066cc;
}

.mc-ai-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 10px;
}

.mc-h2 {
  font-size: 19px;
  line-height: 1.3;
  font-weight: 700;
  margin: 0 0 2px;
  color: #14142b;
  letter-spacing: -0.2px;
}

.mc-lede {
  font-size: 14.5px;
  color: #6e7191;
  margin: 0 0 16px;
}

.mc-tag {
  display: inline-block;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 999px;
  background: #eff0f6;
  /* --text-secondary is only 4.17:1 here; --neutral-700 clears the 4.5:1 this size needs */
  color: #4e4b66;
  white-space: nowrap;
}

.mc-tag--brand {
  background: #d8ebf9;
  color: #003087;
}

.mc-bubble {
  background: #eef3ff;
  border: 1px solid #bdd8f5;
  border-radius: 14px;
  border-bottom-left-radius: 4px;
  padding: 14px 15px;
  font-size: 14.5px;
  color: #002247;
  line-height: 1.55;
  margin-bottom: 14px;
}

.mc-who {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #0052b3;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.mc-asklist {
  margin: 10px 0 0;
  padding-left: 18px;
  font-size: 13.5px;
  color: #002b71;
}

.mc-asklist li {
  margin-bottom: 4px;
}

.mc-center {
  text-align: center;
}

.mc-mic {
  width: 118px;
  height: 118px;
  margin: 8px auto 4px;
  border-radius: 50%;
  background: #0066cc;
  border: 0;
  display: grid;
  place-items: center;
  color: #fff;
  cursor: pointer;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 102, 204, 0.35);
}

.mc-mic:disabled {
  background: #88b9eb;
  cursor: not-allowed;
  box-shadow: none;
}

.mc-mic.is-rec {
  background: #eb5757;
  box-shadow: 0 10px 30px rgba(235, 87, 87, 0.4);
}

.mc-mic.is-rec::before,
.mc-mic.is-rec::after {
  content: '';
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 2px solid rgba(235, 87, 87, 0.5);
  animation: mc-pulse 1.8s ease-out infinite;
}

.mc-mic.is-rec::after {
  animation-delay: 0.9s;
}

@keyframes mc-pulse {
  0% {
    transform: scale(0.92);
    opacity: 0.9;
  }
  100% {
    transform: scale(1.32);
    opacity: 0;
  }
}

.mc-wave {
  display: flex;
  gap: 4px;
  align-items: flex-end;
  justify-content: center;
  height: 30px;
  margin: 14px 0 2px;
}

.mc-wave i {
  width: 4px;
  background: #88b9eb;
  border-radius: 2px;
  height: 8px;
}

.mc-wave.is-on i {
  animation: mc-bounce 0.9s ease-in-out infinite;
  background: #0066cc;
}

.mc-wave.is-on i:nth-child(2n) {
  animation-delay: 0.15s;
}

.mc-wave.is-on i:nth-child(3n) {
  animation-delay: 0.3s;
}

.mc-wave.is-on i:nth-child(4n) {
  animation-delay: 0.45s;
}

@keyframes mc-bounce {
  0%,
  100% {
    height: 7px;
  }
  50% {
    height: 28px;
  }
}

.mc-hint {
  font-size: 12.5px;
  color: #6e7191;
  margin: 6px 0 0;
}

.mc-hint--err,
.mc-hint--danger {
  color: #eb5757;
  font-weight: 500;
}

.mc-hint--warning {
  color: #b9691f;
  font-weight: 500;
}

.mc-linkbtn {
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #0052b3;
  cursor: pointer;
  padding: 4px 0;
}

/* inline in a sentence, so it inherits the surrounding size */
.mc-linkbtn--inline {
  display: inline;
  padding: 0;
  font-size: inherit;
  text-decoration: underline;
}

.mc-trans textarea {
  width: 100%;
  font-family: inherit;
  background: #fff;
  border: 1px solid #d9dbe9;
  border-radius: 8px;
  color: #333;
  font-size: 14.5px;
  min-height: 130px;
  padding: 13px 14px;
  line-height: 1.55;
  resize: vertical;
  appearance: none;
}

.mc-trans textarea:focus {
  outline: 0;
  border-color: #0066cc;
  box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.13);
}

.mc-transhead {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 7px;
}

.mc-lbl2 {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #6e7191;
}

.mc-empty {
  border: 1.5px dashed #d9dbe9;
  border-radius: 14px;
  padding: 26px 18px;
  text-align: center;
  color: #6e7191;
  font-size: 13.5px;
  background: #fff;
}

.mc-empty b {
  display: block;
  color: #333;
  font-size: 14.5px;
  margin-bottom: 4px;
}

.mc-mt8 {
  margin-top: 8px;
}

.mc-mt24 {
  margin-top: 24px;
}
</style>
